import React, {
  createContext,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { unstable_usePrompt as usePrompt } from 'react-router';
import usePreventLeaving from './usePreventLeaving';

const DEFAULT_MESSAGE =
  'You have not yet saved your changes. Are you sure you want to discard them?';

type BeforeLeaveConfirmContextType = {
  addMessage: ({ id, message }: { id: string; message: string }) => void;
  removeMessage: (id: string) => void;
};
const BeforeLeaveConfirmContext =
  createContext<BeforeLeaveConfirmContextType | null>(null);

interface PromptRendererProps {
  message: string;
}
const PromptRenderer = ({ message }: PromptRendererProps) => {
  usePrompt({ when: true, message });
  return null;
};

interface BeforeLeaveConfirmContextProviderProps {
  children: React.ReactNode;
}
const BeforeLeaveConfirmContextProvider = ({
  children,
}: BeforeLeaveConfirmContextProviderProps) => {
  const [messages, setMessages] = useState<{ id: string; message: string }[]>(
    [],
  );

  const activeMessage = messages[0];

  usePreventLeaving({ skip: !activeMessage });

  const value = useMemo(
    () => ({
      addMessage: ({ id, message }: { id: string; message: string }) => {
        setMessages((prev) => [{ id, message }, ...prev]);
      },
      removeMessage: (id: string) => {
        setMessages((prev) => prev.filter((m) => m.id !== id));
      },
    }),
    [setMessages],
  );

  return (
    <BeforeLeaveConfirmContext.Provider value={value}>
      {activeMessage && <PromptRenderer message={activeMessage.message} />}
      {children}
    </BeforeLeaveConfirmContext.Provider>
  );
};

export const useBeforeLeaveConfirm = ({
  skip = false,
  message = DEFAULT_MESSAGE,
} = {}) => {
  const id = useId();
  const context = useContext(BeforeLeaveConfirmContext);

  if (!context) {
    throw new Error(
      'useBeforeLeaveConfirm must be used within a BeforeLeaveConfirmContextProvider',
    );
  }

  useEffect(() => {
    if (!skip) {
      context.addMessage({ id, message });
      return () => {
        context.removeMessage(id);
      };
    }
    return undefined;
  }, [skip, message, context, id]);
};

export default BeforeLeaveConfirmContextProvider;
